import { toast } from '@blocksuite/affine-components/toast';
import {
  AttachmentBlockModel,
  type AttachmentBlockProps,
  AttachmentBlockSchema,
  AttachmentBlockStyles,
} from '@blocksuite/affine-model';
import {
  EMBED_CARD_HEIGHT,
  EMBED_CARD_WIDTH,
} from '@blocksuite/affine-shared/consts';
import {
  type AttachmentUploadedEvent,
  FileSizeLimitProvider,
  TelemetryProvider,
  UploadProgressProvider,
} from '@blocksuite/affine-shared/services';
import {
  formatSize,
  resolveBlobDisplayName,
  resolveBlobDownloadName,
  splitBlobNameForRename,
  uploadBlob,
} from '@blocksuite/affine-shared/utils';
import { Bound, type IVec, Vec } from '@blocksuite/global/gfx';
import type { BlockStdScope } from '@blocksuite/std';
import { GfxControllerIdentifier } from '@blocksuite/std/gfx';
import type { BlockModel } from '@blocksuite/store';

import type { AttachmentBlockComponent } from './attachment-block';

type AttachmentInsertBlockProps = Pick<
  AttachmentBlockProps,
  'name' | 'size' | 'type'
> &
  Partial<Pick<AttachmentBlockProps, 'embed' | 'style'>>;

const ATTACHMENT_MIME_SNIFF_BYTES = 4100;
export const VIDEO_METADATA_TIMEOUT_MS = 3000;

function isPdfFile(file: File): boolean {
  const type = file.type.toLowerCase();
  return type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
}

function isHtmlFile(file: File): boolean {
  const type = file.type.toLowerCase();
  const name = file.name.toLowerCase();
  return (
    type === 'text/html' ||
    type === 'application/xhtml+xml' ||
    name.endsWith('.html') ||
    name.endsWith('.htm')
  );
}

function isVideoFile(file: File): boolean {
  const type = file.type.toLowerCase();
  const name = file.name.toLowerCase();
  return (
    type.startsWith('video/') ||
    name.endsWith('.mp4') ||
    name.endsWith('.mov') ||
    name.endsWith('.m4v') ||
    name.endsWith('.webm') ||
    name.endsWith('.ogv')
  );
}

function isAudioFile(file: File): boolean {
  const type = file.type.toLowerCase();
  const name = file.name.toLowerCase();
  return (
    type.startsWith('audio/') ||
    name.endsWith('.mp3') ||
    name.endsWith('.m4a') ||
    name.endsWith('.aac') ||
    name.endsWith('.wav') ||
    name.endsWith('.ogg') ||
    name.endsWith('.oga') ||
    name.endsWith('.flac')
  );
}

export function getAttachmentBlockPropsForFile(
  file: File
): AttachmentInsertBlockProps {
  const type = file.type || 'application/octet-stream';
  if (isPdfFile(file)) {
    return {
      name: file.name,
      size: file.size,
      type,
      embed: true,
      style: 'pdf' as const,
    };
  }

  if (isHtmlFile(file)) {
    return {
      name: file.name,
      size: file.size,
      type,
      embed: true,
      style: 'html' as const,
    };
  }

  if (isVideoFile(file)) {
    return {
      name: file.name,
      size: file.size,
      type,
      embed: true,
      style: 'video' as const,
    };
  }

  if (isAudioFile(file)) {
    return {
      name: file.name,
      size: file.size,
      type,
      embed: true,
      style: 'audio' as const,
    };
  }

  return {
    name: file.name,
    size: file.size,
    type,
  };
}

export async function getAttachmentBlob(model: AttachmentBlockModel) {
  const { sourceId$, type$ } = model.props;
  const sourceId = sourceId$.peek();
  const type = type$.peek();
  if (!sourceId) return null;

  const doc = model.store;
  const blob = await doc.blobSync.get(sourceId);
  if (!blob) return null;

  return new Blob([blob], { type });
}

export function resolveAttachmentBlockDisplayName(
  model: AttachmentBlockModel
): string {
  return resolveBlobDisplayName({
    blobId: model.props.sourceId,
    occurrenceName: model.props.name,
    mimeType: model.props.type,
  });
}

export function resolveAttachmentBlockDownloadName(
  model: AttachmentBlockModel
): string {
  return resolveBlobDownloadName({
    blobId: model.props.sourceId,
    occurrenceName: model.props.name,
    mimeType: model.props.type,
  });
}

export function getAttachmentBlockExtension(
  model: AttachmentBlockModel
): string {
  return splitBlobNameForRename(
    resolveAttachmentBlockDisplayName(model)
  ).extension.replace(/^\./u, '');
}

export function getVideoEmbedHeightForWidth(
  width: number,
  videoSize: { width: number; height: number }
) {
  if (width <= 0 || videoSize.width <= 0 || videoSize.height <= 0) {
    return EMBED_CARD_HEIGHT.video;
  }

  return Math.round((width * videoSize.height) / videoSize.width);
}

export function readVideoSize(file: File | Blob) {
  return new Promise<{ width: number; height: number }>(resolve => {
    if (!file.type.startsWith('video/')) {
      resolve({ width: 0, height: 0 });
      return;
    }

    const video = document.createElement('video');
    const url = URL.createObjectURL(file);
    let settled = false;
    const settle = (size: { width: number; height: number }) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      video.removeAttribute('src');
      video.load();
      URL.revokeObjectURL(url);
      resolve(size);
    };
    const timeout = window.setTimeout(
      () => settle({ width: 0, height: 0 }),
      VIDEO_METADATA_TIMEOUT_MS
    );

    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      settle({
        width: video.videoWidth,
        height: video.videoHeight,
      });
    };
    video.onerror = () => {
      settle({ width: 0, height: 0 });
    };
    video.src = url;
  });
}

async function updateVideoAttachmentBoundFromFile(
  std: BlockStdScope,
  blockId: string,
  file: File
) {
  if (!isVideoFile(file)) return;

  const block = std.store.getBlock(blockId);
  const model = block?.model;
  if (!(model instanceof AttachmentBlockModel)) return;

  const bound = Bound.fromXYWH(model.deserializedXYWH);
  if (bound.w <= 0) return;

  const videoSize = await readVideoSize(file);
  const nextHeight = getVideoEmbedHeightForWidth(bound.w, videoSize);
  if (nextHeight <= 0 || Math.abs(bound.h - nextHeight) < 1) return;

  const nextBound = bound.clone();
  nextBound.h = nextHeight;
  std.store.updateBlock(model, {
    xywh: nextBound.serialize(),
  });
}

/**
 * Since the size of the attachment may be very large,
 * the download process may take a long time!
 */
export function downloadAttachmentBlob(block: AttachmentBlockComponent) {
  const { host, model, blobUrl, resourceController } = block;

  if (resourceController.state$.peek().downloading) {
    toast(host, 'Download in progress...');
    return;
  }

  const displayName = resolveAttachmentBlockDisplayName(model);
  const downloadName = resolveAttachmentBlockDownloadName(model);
  const shortName =
    displayName.length < 20 ? displayName : displayName.slice(0, 20) + '...';

  if (!blobUrl) {
    toast(host, `Failed to download ${shortName}!`);
    return;
  }

  resourceController.updateState({ downloading: true });

  toast(host, `Downloading ${shortName}`);

  const tmpLink = document.createElement('a');
  const event = new MouseEvent('click');
  tmpLink.download = downloadName;
  tmpLink.href = blobUrl;
  tmpLink.dispatchEvent(event);
  tmpLink.remove();

  resourceController.updateState({ downloading: false });
}

export async function refreshData(block: AttachmentBlockComponent) {
  const model = block.model;
  const type = model.props.type$.peek();

  await block.resourceController.refreshUrlWith(type);
}

export async function getFileType(file: File) {
  if (file.type) return file.type;

  // If the file type is not available, sniff only the magic-byte header.
  const header = await file.slice(0, ATTACHMENT_MIME_SNIFF_BYTES).arrayBuffer();
  const FileType = await import('file-type');
  const fileType = await FileType.fileTypeFromBuffer(new Uint8Array(header));
  return fileType?.mime ?? '';
}

function hasExceeded(
  std: BlockStdScope,
  files: File[],
  maxFileSize = std.get(FileSizeLimitProvider).maxFileSize
) {
  const exceeded = files.some(file => file.size > maxFileSize);

  if (exceeded) {
    const size = formatSize(maxFileSize);
    toast(std.host, `You can only upload files less than ${size}`);
  }

  return exceeded;
}

/**
 * Upload a file in the background and update the block's sourceId when done.
 * Reports progress via UploadProgressProvider.
 */
function uploadInBackground(
  std: BlockStdScope,
  blockId: string,
  file: File,
  mode: 'doc' | 'whiteboard' = 'doc'
): void {
  const progressService = std.getOptional(UploadProgressProvider);

  // Initialize progress at 0
  progressService?.set(blockId, 0, file.size);

  let type = file.type;
  let category: AttachmentUploadedEvent['category'] = 'success';

  uploadBlob(std, file, (bytesUploaded, bytesTotal) => {
    progressService?.set(blockId, bytesUploaded, bytesTotal);
  })
    .then(async sourceId => {
      type = await getFileType(file);

      // Update block with sourceId — triggers ResourceController refresh
      std.store.updateBlock(blockId, { sourceId, type });
    })
    .catch(err => {
      category = 'failure';
      console.error(`[attachment] Upload failed for block ${blockId}:`, err);
      toast(std.host, `Upload failed: ${file.name}`);
    })
    .finally(() => {
      progressService?.delete(blockId);
      std.getOptional(TelemetryProvider)?.track('AttachmentUploadedEvent', {
        page: `${mode} editor`,
        module: 'attachment',
        segment: mode,
        control: 'uploader',
        type,
        category,
      });
    });
}

/**
 * Add a new attachment block before / after the specified block.
 * Block appears instantly with file metadata; upload runs in background.
 */
export function addSiblingAttachmentBlocks(
  std: BlockStdScope,
  files: File[],
  targetModel: BlockModel,
  placement: 'before' | 'after' = 'after',
  embed?: boolean
): string[] {
  if (!files.length) return [];

  if (hasExceeded(std, files)) return [];

  const flavour = AttachmentBlockSchema.model.flavour;

  // Create blocks instantly with file metadata (no sourceId yet)
  const blockIds = std.store.addSiblingBlocks(
    targetModel,
    files.map(file => {
      const attachmentProps = getAttachmentBlockPropsForFile(file);
      return {
        flavour,
        ...attachmentProps,
        ...(embed !== undefined ? { embed } : {}),
      };
    }),
    placement
  );

  // Upload each file in background, update sourceId when done
  files.forEach((file, i) => {
    uploadInBackground(std, blockIds[i], file, 'doc');
  });

  return blockIds;
}

/**
 * Add attachment blocks to the edgeless canvas.
 * Blocks appear instantly with file metadata; upload runs in background.
 */
export function addAttachments(
  std: BlockStdScope,
  files: File[],
  point?: IVec,
  shouldTransformPoint?: boolean
): string[] {
  if (!files.length) return [];

  if (hasExceeded(std, files)) return [];

  const gfx = std.get(GfxControllerIdentifier);
  let { x, y } = gfx.viewport.center;
  if (point) {
    shouldTransformPoint = shouldTransformPoint ?? true;
    if (shouldTransformPoint) {
      [x, y] = gfx.viewport.toModelCoord(...point);
    } else {
      [x, y] = point;
    }
  }

  const xy = [x, y];
  const gap = 32;
  const flavour = AttachmentBlockSchema.model.flavour;
  const nextLayerIndex = gfx.layer.createIndexGenerator();

  // Create blocks instantly with file metadata (no sourceId yet)
  const blocks = files.map((file, index) => {
    const center = Vec.addScalar(xy, index * gap);
    const attachmentProps = getAttachmentBlockPropsForFile(file);
    const style = attachmentProps.style ?? AttachmentBlockStyles[1];
    const width = EMBED_CARD_WIDTH[style];
    const height = EMBED_CARD_HEIGHT[style];
    const xywh = Bound.fromCenter(center, width, height).serialize();
    return {
      flavour,
      blockProps: {
        ...attachmentProps,
        style,
        xywh,
        index: nextLayerIndex(),
      },
    };
  });

  const blockIds = std.store.addBlocks(blocks, gfx.surface);

  gfx.selection.set({
    elements: blockIds,
    editing: false,
  });

  // Upload each file in background, update sourceId when done
  files.forEach((file, i) => {
    uploadInBackground(std, blockIds[i], file, 'whiteboard');
    updateVideoAttachmentBoundFromFile(std, blockIds[i], file).catch(
      console.error
    );
  });

  return blockIds;
}
